import { API_BASE_URL } from '../config/api';

export interface SePayCreateRequest {
  amount: number;
  description: string;
  order_code: string;
  campaign_id?: number;
  ad_type?: string;
  user_id: number;
  buyer_name?: string;
  buyer_email?: string;
  buyer_phone?: string;
  return_url?: string;
  cancel_url?: string;
}

export interface SePayPaymentResponse {
  id: string;
  checkout_url?: string;
  order_code: string;
  payment_link_id?: string;
  qr_code_url?: string;
  amount: number;
  status: string;
  payment_code?: string;
  sepay_order_code?: string;
  account_number?: string;
  bank?: string;
  account_name?: string;
}

export interface SePayStatusResponse {
  order_code: string;
  payment_link_id?: string;
  status: string;
  amount?: number;
}

export interface LandlordSePayAccount {
  configured: boolean;
  sepay_bank?: string | null;
  sepay_account?: string | null;
  sepay_webhook_api_key?: string | null;
}

export type LandlordSePayAccountInput = {
  sepay_bank: string;
  sepay_account: string;
  sepay_webhook_api_key: string;
};

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'Content-Type': 'application/json',
  };
}

async function readError(response: Response): Promise<string> {
  try {
    const error = await response.json();
    const detail = error?.detail;
    if (typeof detail === 'string') return detail;
    return 'Không tạo được thanh toán SePay';
  } catch {
    return 'Không tạo được thanh toán SePay';
  }
}

export const sepayService = {
  generateOrderCode(prefix: string): string {
    const clean = (prefix || 'PAY').replace(/[^A-Za-z]/g, '').toUpperCase() || 'PAY';
    return `${clean}${Date.now().toString().slice(-8)}`;
  },

  async createPaymentLink(request: SePayCreateRequest): Promise<SePayPaymentResponse> {
    const response = await fetch(`${API_BASE_URL}/sepay/create-payment-link`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(await readError(response));
    }
    return response.json();
  },

  async getOrderStatus(code: string, signal?: AbortSignal): Promise<{ status: string; code?: string }> {
    const response = await fetch(
      `${API_BASE_URL}/sepay/orders/${encodeURIComponent(code)}/status`,
      { headers: authHeaders(), signal }
    );
    if (!response.ok) {
      throw new Error('Failed to check payment status');
    }
    return response.json();
  },

  async getPaymentStatus(orderCode: string): Promise<SePayStatusResponse> {
    const response = await fetch(
      `${API_BASE_URL}/sepay/payment-status?order_code=${encodeURIComponent(orderCode)}`,
      { headers: authHeaders() }
    );
    if (!response.ok) {
      throw new Error('Failed to check payment status');
    }
    return response.json();
  },

  isPaymentSuccessful(status?: string): boolean {
    const value = (status || '').toLowerCase();
    return value === 'paid' || value === 'completed';
  },

  isPaymentCancelled(status?: string): boolean {
    const value = (status || '').toUpperCase();
    return value === 'CANCELLED';
  },

  async getLandlordAccount(): Promise<LandlordSePayAccount> {
    const response = await fetch(`${API_BASE_URL}/sepay/landlord-account`, {
      headers: authHeaders(),
    });
    if (!response.ok) {
      throw new Error(await readError(response));
    }
    return response.json();
  },

  async saveLandlordAccount(payload: LandlordSePayAccountInput): Promise<LandlordSePayAccount> {
    const response = await fetch(`${API_BASE_URL}/sepay/landlord-account`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(await readError(response));
    }
    return response.json();
  },
};
